
import { Options, Vue, prop } from "vue-class-component";
import { LastBalanceController } from "@/app/ui/controllers/LastBalanceController";
import { LastBalanceDownloadRequest } from "@/domain/entities/LastBalance";
import { formatDateNumber, formatInputNumberOnly, formatInputMoney, formatInputMoneyWithDecimal } from "@/app/infrastructures/misc/Utils";
import { AccountController } from "@/app/ui/controllers/AccountController";

class Props {
  show = prop<boolean>({
    type: Boolean,
    default: false
  });
  modalTitle = prop<string>({
    type: String,
    default: ""
  });
  modalData = prop<LastBalanceDownloadRequest>({
    type: LastBalanceDownloadRequest,
    default: new LastBalanceDownloadRequest()
  })
}

@Options({
  components: {},
  watch: {
    show(newVal: boolean) {
      if (newVal) {
        (this as any).initFromProps();
      }
    }
  }
})
export default class ModalDownload extends Vue.with(Props) {
  props: any = this.$props;
  balanceStatus = { name: "Semua", value: "all" }
  startDate: Date | null = null;
  endDate: Date | null = null;
  balanceRangeFrom = "";
  balanceRangeTo = "";
  errorBalanceRangeFrom = "";
  errorBalanceRangeTo = "";

  // Dropdown options
  balanceOptions = [
    { name: "Semua", value: "all" },
    { name: "Saldo minus", value: "minus" },
    { name: "Saldo plus", value: "plus" }
  ];

  get accountIsNonForeign() {
    return !AccountController.accountData.accountIsForeign;
  }

  get isValidDownload() {
    return (
      this.balanceStatus.value &&
      this.startDate &&
      this.endDate &&
      this.balanceRangeFrom &&
      this.balanceRangeTo &&
      !this.errorBalanceRangeFrom &&
      !this.errorBalanceRangeTo
    );
  }

  initFromProps() {
    const data = this.modalData || new LastBalanceDownloadRequest();

    this.balanceStatus =
      this.balanceOptions.find((option) => option.value === data.balanceStatus) ||
      { name: "Semua", value: "all" };

    this.balanceRangeFrom = data.balanceFrom || "";
    this.balanceRangeTo = data.balanceTo || "";
    this.errorBalanceRangeFrom = "";
    this.errorBalanceRangeTo = "";

    this.startDate = data.lastTrxDateFrom ? new Date(data.lastTrxDateFrom) : null;
    this.endDate = data.lastTrxDateTo ? new Date(data.lastTrxDateTo) : null;

    if (this.balanceRangeFrom) {
      this.validateRangeSaldo(this.balanceRangeFrom, "from");
    }
    if (this.balanceRangeTo) {
      this.validateRangeSaldo(this.balanceRangeTo, "to");
    }
  }

  onCloseModal() {
    this.$emit("update:show", false);
    this.$emit("close");
  }

  setPeriode(value: Date[]): void {
    this.startDate = value[0] ?? null;
    this.endDate = value[1] ?? null;
  }

  onChangeBalance({ name, value }: { name: string, value: string }) {
    this.balanceStatus = { name, value };
  }

  formatEstimationPrice(value: string): string {
    return this.accountIsNonForeign
      ? this.formatRupiah(value)
      : this.formatRinggit(value);
  }

  formatRupiah(value: string): string {
    return formatInputNumberOnly(formatInputMoney(value.replace(/[\D]+/g, "")));
  }

  formatRinggit(value: string): string {
    return formatInputNumberOnly(formatInputMoneyWithDecimal(value));
  }

  parseAmount(value: string): number {
    return Number(value.replace(/[\D]+/g, "")) || 0;
  }

  validateRangeSaldo(val: string, type: string) {
    const formattedVal = this.formatEstimationPrice(val);

    if (type === "from") {
      this.balanceRangeFrom = formattedVal;
      this.errorBalanceRangeFrom = "";

      if (this.balanceRangeTo) {
        const fromNum = this.parseAmount(formattedVal);
        const toNum = this.parseAmount(this.balanceRangeTo);

        if (fromNum > toNum) {
          this.errorBalanceRangeFrom = "Range awal tidak boleh lebih besar dari range akhir!";
          return;
        }

        if (this.errorBalanceRangeTo) {
          this.errorBalanceRangeTo = "";
        }
      }
    } else {
      this.balanceRangeTo = formattedVal;
      this.errorBalanceRangeTo = "";

      if (this.balanceRangeFrom) {
        const fromNum = this.parseAmount(this.balanceRangeFrom);
        const toNum = this.parseAmount(formattedVal);

        if (toNum < fromNum) {
          this.errorBalanceRangeTo = "Range akhir tidak boleh lebih kecil dari range awal!";
          return;
        }

        if (this.errorBalanceRangeFrom) {
          this.errorBalanceRangeFrom = "";
        }
      }
    }
  }

  async onSubmit() {
    const params = new LastBalanceDownloadRequest({
      ...this.modalData,
      actorType: this.modalData.actorType.replace("_cod", ""),
      balanceStatus: this.balanceStatus.value,
      balanceFrom: this.balanceRangeFrom,
      balanceTo: this.balanceRangeTo,
      lastTrxDateFrom: formatDateNumber(this.startDate),
      lastTrxDateTo: formatDateNumber(this.endDate)
    });
    const result: any = await LastBalanceController.downloadLastBalance(params);
    if (result && result.success) {
      this.onCloseModal();
    }
  }
}
